/**
 * Where the ID token lives between page loads.
 *
 * **IndexedDB, not localStorage** — carried over from DragDuro deliberately,
 * not as a preference. localStorage is synchronous and blocks the main thread,
 * but the reason that actually matters here is reach: `localStorage` is a
 * single flat namespace any script on the origin can enumerate in one line
 * (`localStorage.idToken`), which makes a token sitting in it the first thing
 * an injected script finds. IndexedDB is no security boundary either — same
 * origin, same access — but it is asynchronous, structured, and not the thing
 * every credential-scraping snippet reaches for by default.
 *
 * Every helper below swallows its own failures. Private-browsing modes and
 * storage-disabled browsers throw on `indexedDB.open` itself, and a browser
 * that cannot persist a token should degrade to "signs in again next visit",
 * never to "the app throws on boot".
 */

const DB_NAME = 'svcdue-auth';
const DB_VERSION = 1;
const STORE = 'kv';
const KEY = 'idToken';

/**
 * One transaction, one callback, one promise.
 *
 * The result is read in `oncomplete` rather than in the request's own
 * `onsuccess`: by the time a transaction commits its requests have necessarily
 * finished, so this reads a settled `IDBRequest.result` and needs only one
 * success path instead of two. `db.close()` runs on both paths so a version
 * upgrade in another tab is never left blocked by this one.
 */
function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return new Promise<T | null>((resolve, reject) => {
    let open: IDBOpenDBRequest;
    try {
      open = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      // Private mode / storage disabled: the synchronous call itself throws.
      reject(err);
      return;
    }

    open.onupgradeneeded = () => {
      // Out-of-line keys: this store holds one value under one known key, so a
      // keyPath would only be ceremony.
      if (!open.result.objectStoreNames.contains(STORE)) open.result.createObjectStore(STORE);
    };
    open.onerror = () => reject(open.error);
    open.onblocked = () => reject(new Error('IndexedDB upgrade blocked by another tab'));

    open.onsuccess = () => {
      const db = open.result;
      let request: IDBRequest<T>;
      try {
        request = fn(db.transaction(STORE, mode).objectStore(STORE));
      } catch (err) {
        db.close();
        reject(err);
        return;
      }
      request.transaction!.oncomplete = () => {
        db.close();
        resolve(request.result ?? null);
      };
      request.transaction!.onerror = () => {
        db.close();
        reject(request.transaction!.error);
      };
    };
  });
}

export async function readToken(): Promise<string | null> {
  try {
    const value = await withStore<string>('readonly', (store) => store.get(KEY));
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
}

export async function writeToken(token: string): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.put(token, KEY));
  } catch {
    // Memory-only session. The user signs in again next visit, which is a far
    // better outcome than an unhandled rejection on every token refresh.
  }
}

export async function clearToken(): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.delete(KEY));
  } catch {
    // Nothing to do — sign-out has already cleared the in-memory copy, which is
    // what every read actually consults.
  }
}
